import { logger } from '@automata/utils';

import { CodeTaskSidebar } from './CodeTaskSideBar';
import { ContinuousLoadingSidebar } from './ContinuousLoadingSidebar';
import { ExportTaskSidebar } from './ExportTaskSideBar';
import { LabwareSidebar } from './LabwareSidebar';
import { LogicalSidebar } from './LogicalSidebar';
import { TaskSidebar } from './TaskSidebar';
import { TimeConstraintSidebar } from './TimeConstraintSidebar';
import {
  selectSelectedNodeForPropertiesSidebar,
  selectSelectedTimeConstraintIdForPropertiesSidebar,
} from '../../features/graph/selectors';
import { selectPropertiesSidebarIsOpen } from '../../features/selectors';
import { selectTaskPropertiesStrict } from '../../features/workflow/selectors';
import { useAppSelector } from '../../hooks';
import { AllTaskProperties, NodeType, PropertiesType } from '../../types';

const ConnectedTaskPropertiesSidebar = ({ id }: { id: string }) => {
  const taskProperties: AllTaskProperties | undefined = useAppSelector(
    selectTaskPropertiesStrict
  )[id];

  if (!taskProperties) return null;

  if (taskProperties.propertiesType === PropertiesType.Code) {
    return <CodeTaskSidebar selectedNodeId={id} properties={taskProperties} />;
  }

  if (taskProperties.propertiesType === PropertiesType.DataExport) {
    return (
      <ExportTaskSidebar selectedNodeId={id} properties={taskProperties} />
    );
  }

  if (taskProperties.propertiesType === PropertiesType.Action) {
    return <TaskSidebar selectedNodeId={id} properties={taskProperties} />;
  }

  if (taskProperties.propertiesType === PropertiesType.Conditional) {
    return <LogicalSidebar selectedNodeId={id} />;
  }

  if (taskProperties.propertiesType === PropertiesType.ContinuousLoading) {
    return (
      <ContinuousLoadingSidebar
        selectedNodeId={id}
        properties={taskProperties}
      />
    );
  }

  return null;
};

export const ConnectedPropertiesSidebar = (): JSX.Element | null => {
  const selectedNode = useAppSelector(selectSelectedNodeForPropertiesSidebar);
  const selectedTimeConstraintId = useAppSelector(
    selectSelectedTimeConstraintIdForPropertiesSidebar
  );
  const isOpen = useAppSelector(selectPropertiesSidebarIsOpen);

  if (!isOpen) return null;

  if (selectedTimeConstraintId !== null) {
    return (
      <TimeConstraintSidebar
        key={selectedTimeConstraintId}
        timeConstraintId={selectedTimeConstraintId}
      />
    );
  }

  if (selectedNode !== null) {
    const { nodeType, id } = selectedNode;

    if (nodeType === NodeType.LABWARE) {
      return <LabwareSidebar key={id} selectedNodeId={id} />;
    }

    if (nodeType === NodeType.TASK) {
      return <ConnectedTaskPropertiesSidebar key={id} id={id} />;
    }
    if (nodeType === NodeType.CONDITIONAL_TASK) {
      // TODO: Implement ConditionalTaskSidebar
      return null;
    }

    nodeType satisfies never;

    logger.warn('node selected but has no properties', selectedNode);
    return null;
  }

  return null;
};
